import {
    ActionValue,
    notificationActions,
} from "../../redux/notification-slice";
import { ContentMessageBox } from "../windows/WindowMessageBox/modelsMessageBox";
import { useAppDispatch } from "./hooks";

export const useNotification = () => {
    const dispatch = useAppDispatch();

    const clearNotification = async (returnValue: ActionValue) => {
        await dispatch(notificationActions.clearNotification(returnValue));
        // console.log(`Return value ${returnValue}`);
    };

    const handleButtonClick = async (
        valueAfterClick: ActionValue,
        resolve: (value: ActionValue) => void
    ) => {
        try {
            await clearNotification(valueAfterClick);
            // console.log("Notification cleared");
            resolve(valueAfterClick);
        } catch (error) {
            console.error("Error in handleButtonClick:", error);
        }
    };

    const createActionButtons = (
        buttons: ContentMessageBox["actionButtons"],
        resolve: (value: ActionValue) => void
    ) => {
        return (
            buttons?.map((button) => {
                const originalOnClick = button.onClick;
                button.onClick = () => {
                    if (originalOnClick) {
                        originalOnClick();
                    }
                    handleButtonClick(
                        button.valueAfterClick as ActionValue,
                        resolve
                    );
                };
                return button;
            }) ?? [
                {
                    name: "yes",
                    valueAfterClick: "yes",
                    role: "primary",
                    onClick: () => handleButtonClick("yes", resolve),
                },
                {
                    name: "no",
                    valueAfterClick: "no",
                    onClick: () => handleButtonClick("no", resolve),
                },
            ]
        );
    };

    const globalEventListener = async (
        e: MouseEvent | KeyboardEvent,
        resolve: (value: ActionValue) => void
    ) => {
        // Проверка на ESC
        if (e instanceof KeyboardEvent && e.key === "Escape") {
            await clearNotification("no");
            resolve("no");
            return;
        }

        // Проверка на клик по BACKDROP
        if (
            e instanceof MouseEvent &&
            (e.target as HTMLElement)?.id === "BACKDROP"
        ) {
            await clearNotification("no");
            resolve("no");
            return;
        }

        // Проверка на клик по CAPTION_CLOSE или вложенному элементу в нем
        if (
            e instanceof MouseEvent &&
            (e.target as HTMLElement)?.closest("#CAPTION_CLOSE")
        ) {
            await clearNotification("no");
            resolve("no");
            return;
        }
    };

    const AttentionMessageBox = async (notification: ContentMessageBox) => {
        return new Promise<ActionValue>(async (resolve, reject) => {
            const actionButtons = createActionButtons(
                notification.actionButtons,
                resolve
            );

            const eventListener = (e: any) => globalEventListener(e, resolve);

            document.body.addEventListener("keydown", eventListener);
            document.body.addEventListener("click", eventListener);

            dispatch(
                notificationActions.addNotification({
                    stateWindow: "attention",
                    actionButtons,
                    ...notification,
                })
            );

            return () => {
                document.body.removeEventListener("keydown", eventListener);
                document.body.removeEventListener("click", eventListener);
            };
        });
    };

    const ConfirmationMessageBox = async (notification: ContentMessageBox) => {
        return new Promise<ActionValue>(async (resolve, reject) => {
            const actionButtons = createActionButtons(
                notification.actionButtons,
                resolve
            );

            const eventListener = (e: any) => globalEventListener(e, resolve);

            document.body.addEventListener("keydown", eventListener);
            document.body.addEventListener("click", eventListener);

            dispatch(
                notificationActions.addNotification({
                    stateWindow: "confirmation",
                    actionButtons,
                    ...notification,
                })
            );

            return () => {
                document.body.removeEventListener("keydown", eventListener);
                document.body.removeEventListener("click", eventListener);
            };
        });
    };

    const InformationMessageBox = async (notification: ContentMessageBox) => {
        return new Promise<ActionValue>(async (resolve, reject) => {
            const actionButtons = createActionButtons(
                [
                    {
                        name: "ok",
                        valueAfterClick: "ok",
                        role: "primary",
                        onClick: () => handleButtonClick("ok", resolve),
                    },
                ],
                resolve
            );

            const eventListener = (e: any) => globalEventListener(e, resolve);

            document.body.addEventListener("keydown", eventListener);
            document.body.addEventListener("click", eventListener);

            dispatch(
                notificationActions.addNotification({
                    stateWindow: "information",
                    caption: {
                        hideCaptionButtons: true,
                    },
                    actionButtons,
                    ...notification,
                })
            );

            return () => {
                document.body.removeEventListener("keydown", eventListener);
                document.body.removeEventListener("click", eventListener);
            };
        });
    };

    return {
        AttentionMessageBox,
        ConfirmationMessageBox,
        InformationMessageBox,
        clearNotification,
    } as const;
};
